import useAuth from "./hooks/useAuth";
import { useState, useEffect } from "react";
import CheckUserLanguages from "./CheckUserLanguages";
import CategoryDropdownDates from "./CategoryDropdownDates";
import UserSentences from "./UserSentences";
import UserWords from "./UserWords";
import UserPhonemes from "./UserPhonemes";
import Select from 'react-select';

const UserStats = () => {
    const { auth } = useAuth();
    const stats = [
        { value: "Sentences", label: 'Sentences' },
        { value: "Words", label: 'Words' },
        { value: "Phonemes", label: 'Phonemes' }
    ];
    
    const [language, setLanguage] = useState();
    const [date, setDate] = useState('Select date');
    const [type, setType] = useState(stats[0]);
    const [representation, setRepresentation] = useState('Dynamic');
    const [autoSelected, setAutoSelected] = useState(false);

    const handleLanguageChange = (e) => {
        setLanguage(e.value);
        setDate('All records');
        setRepresentation('Dynamic');
        
        // Auto-select Sentences when language is chosen
        if (!autoSelected) {
            setAutoSelected(true);
        }
    };

    const handleDateChange = (e) => {
        setDate(e.value);
    };

    const handleTypeChange = (e) => {
        const newType = e.value;
        setType(e);
        
        // When switching to Sentences or Phonemes, set date to "All records" and use Dynamic view
        if (newType === 'Sentences' || newType === 'Phonemes') {
            setDate('All records');
            setRepresentation('Dynamic');
        } else {
            setDate('Select date');
        }
    };

    // Determine if date selector should be disabled
    const isDateDisabled = !language || 
        (type?.value === 'Phonemes' && representation === 'Dynamic') || 
        (type?.value === 'Sentences' && (representation === 'Dynamic' || representation === 'Intensity'));

    // Check if we should show results (has valid date selection)
    const hasValidDate = date !== 'Select date';
    const hasValidFilters = language && 
        ((type?.value === 'Sentences' && hasValidDate) || 
         (type?.value === 'Phonemes' && hasValidDate) ||
         (type?.value === 'Words' && hasValidDate));

    // Auto-select Sentences when language is first chosen
    useEffect(() => {
        if (language && !autoSelected) {
            setAutoSelected(true);
        }
    }, [language, autoSelected]);

    // Get available representation options based on type
    const getRepresentationOptions = () => {
        if (type?.value === 'Sentences') {
            return [
                { value: 'Dynamic', label: 'Dynamic View', description: 'All records, date selection disabled' },
                { value: 'Snapshot', label: 'Snapshot View', description: 'Select specific time period' },
                { value: 'Intensity', label: 'Intensity View', description: 'All records, date selection disabled' }
            ];
        } else if (type?.value === 'Phonemes') {
            return [
                { value: 'Dynamic', label: 'Dynamic View', description: 'All records, date selection disabled' },
                { value: 'Snapshot', label: 'Snapshot View', description: 'Select specific time period' }
            ];
        }
        return [];
    };

    const handleRepresentationChange = (e) => {
        const newRepresentation = e.target.value;
        setRepresentation(newRepresentation);
        
        // Auto-set date based on representation
        if (newRepresentation === 'Dynamic' || newRepresentation === 'Intensity') {
            setDate('All records');
        } else {
            setDate('Select date'); // Reset to "Select date" for Snapshot view
        }
    };

    const representationOptions = getRepresentationOptions();

    return (
        <div className="w-full h-full min-h-screen bg-gray-50">
            {/* Main Content Container - Full Width */}
            <div className="w-full max-w-none mx-auto">
                {/* Header Section */}
                <div className="bg-white border-b border-gray-200 shadow-sm">
                    <div className="px-6 py-8">
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-3 mb-3">
                                <div className="bg-blue-100 p-3 rounded-xl">
                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                    </svg>
                                </div>
                                <h1 className="text-3xl font-bold text-gray-800">Check your stats</h1>
                            </div>
                            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                                Track your pronunciation progress
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="px-6 py-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                    <div className="max-w-7xl mx-auto">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                                </svg>
                                Filter Analytics
                            </h3>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Language Selector */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                        Language
                                    </label>
                                    <CheckUserLanguages 
                                        selectHandler={handleLanguageChange} 
                                    />
                                </div>

                                {/* Date Selector */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                        Time Period
                                        {isDateDisabled && (
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto: All records</span>
                                        )}
                                    </label>
                                    <CategoryDropdownDates 
                                        language={language} 
                                        selectHandler={handleDateChange}
                                        disabled={isDateDisabled}
                                        value={date}
                                    />
                                    {isDateDisabled && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Date selection is automatically set to "All records" for {type?.value === 'Sentences' && representation === 'Intensity' ? 'intensity' : 'dynamic'} analysis
                                        </p>
                                    )}
                                </div>

                                {/* Category Selector */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                        Analysis Type
                                    </label>
                                    <Select 
                                        onChange={handleTypeChange}
                                        options={stats}
                                        value={type}
                                        className="text-sm"
                                        styles={{
                                            control: (base, state) => ({
                                                ...base,
                                                minHeight: '44px',
                                                borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                                                borderRadius: '8px',
                                                backgroundColor: 'white',
                                                boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                                                '&:hover': {
                                                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                borderRadius: '8px',
                                                border: '1px solid #e5e7eb',
                                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                                zIndex: 50
                                            })
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Representation Selector (only for Sentences/Phonemes) */}
                            {(type?.value === 'Sentences' || type?.value === 'Phonemes') && (
                                <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <label className="block text-sm font-medium text-purple-800 mb-2 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                        View Type
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {representationOptions.map((option) => (
                                            <label key={option.value} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-purple-100 hover:bg-purple-50 transition-colors cursor-pointer">
                                                <input
                                                    type="radio"
                                                    value={option.value}
                                                    checked={representation === option.value}
                                                    onChange={handleRepresentationChange}
                                                    className="text-purple-600 focus:ring-purple-500 mt-0.5"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-800 text-sm">
                                                        {option.label}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {option.description}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Current Selection Display */}
                            {hasValidFilters && (
                                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex flex-col sm:flex-row items-center gap-3 text-sm">
                                        <span className="font-semibold text-blue-800 flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Current Analysis:
                                        </span>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                                                {type?.label}
                                            </span>
                                            {(type?.value === 'Sentences' || type?.value === 'Phonemes') && (
                                                <span className="bg-white px-3 py-1 rounded-full border border-purple-200 font-medium text-purple-700 shadow-sm">
                                                    {representation} View
                                                </span>
                                            )}
                                            <span className="text-blue-600 mx-1">•</span>
                                            <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                                                {language}
                                            </span>
                                            <span className="text-blue-600 mx-1">•</span>
                                            <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                                                {date}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Results Section - Full Width */}
                <div className="flex-1 w-full">
                    {hasValidFilters ? (
                        <div className="w-full">
                            {/* Content Header */}
                            <div className="bg-white border-b border-gray-200 px-6 py-4">
                                <div className="max-w-7xl mx-auto">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                                            <div className="bg-blue-100 p-2 rounded-lg">
                                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                            </div>
                                            {type?.label} Analytics
                                        </h3>
                                        <div className="flex items-center gap-4 mt-2 sm:mt-0">
                                            <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                                <span className="font-medium">{language}</span>
                                                <span className="mx-2">•</span>
                                                <span>{date}</span>
                                                {(type?.value === 'Sentences' || type?.value === 'Phonemes') && (
                                                    <>
                                                        <span className="mx-2">•</span>
                                                        <span className="font-medium">{representation} View</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content Area - Full Width */}
                            <div className="w-full">
                                {type?.value === 'Words' && (
                                    <UserWords 
                                        language={language} 
                                        date={date} 
                                        key={`words-${language}-${date}`}
                                    />
                                )}
                                
                                {type?.value === 'Sentences' && (
                                    <UserSentences 
                                        language={language} 
                                        date={date}
                                        representation={representation}
                                        key={`sentences-${language}-${date}-${representation}`}
                                    />
                                )}
                                
                                {type?.value === 'Phonemes' && (
                                    <UserPhonemes 
                                        language={language} 
                                        date={date}
                                        representation={representation}
                                        key={`phonemes-${language}-${date}-${representation}`}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Empty State - Different states based on what's missing */
                        !language ? (
                            <div className="flex items-center justify-center py-20 px-6">
                                <div className="text-center max-w-2xl">
                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
                                        <div className="text-gray-400 text-8xl mb-6">👤</div>
                                        <h3 className="text-2xl font-bold text-gray-700 mb-4">
                                            Select Analysis Parameters
                                        </h3>
                                        <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                                            Choose a language to start analyzing your statistics
                                        </p>
                                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                Select target language
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                Auto: All records
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                                Auto: Sentences analysis
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : !hasValidDate ? (
                            <div className="flex items-center justify-center py-20 px-6">
                                <div className="text-center max-w-2xl">
                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
                                        <div className="text-gray-400 text-8xl mb-6">📅</div>
                                        <h3 className="text-2xl font-bold text-gray-700 mb-4">
                                            Select Time Period
                                        </h3>
                                        <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                                            Choose a time period to view {language} {type?.label?.toLowerCase()} statistics
                                        </p>
                                        <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm text-gray-500">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                Select a specific time period
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                                {type?.label} - {representation} View
                                            </div>
                                        </div>
                                        {representation === 'Snapshot' && (
                                            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 max-w-md mx-auto">
                                                <p className="text-blue-600 text-sm flex items-center gap-2">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span><span className="font-semibold">{language}</span> and <span className="font-semibold">{type?.label}</span> selected. Now choose a time period to see the statistics.</span>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* This case should not happen with current logic, but keeping as fallback */
                            <div className="flex items-center justify-center py-20 px-6">
                                <div className="text-center max-w-2xl">
                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
                                        <div className="text-gray-400 text-8xl mb-6">📊</div>
                                        <h3 className="text-2xl font-bold text-gray-700 mb-4">
                                            Ready to Analyze
                                        </h3>
                                        <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                                            Configure your analysis settings to view statistics
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserStats;